// Animated money/number display: tweens between values and flashes
// green/red on change so the player feels income and expenses.

import { useEffect, useRef, useState } from 'react'
import { animate, useReducedMotion } from 'framer-motion'

interface AnimatedNumberProps {
  value: number
  suffix?: string
  className?: string
  style?: React.CSSProperties
  flash?: boolean // flash color on delta (default true)
}

export function AnimatedNumber({ value, suffix = '', className, style, flash = true }: AnimatedNumberProps) {
  const [display, setDisplay] = useState(value)
  const [delta, setDelta] = useState<'up' | 'down' | null>(null)
  const previous = useRef(value)
  const reduced = useReducedMotion()

  useEffect(() => {
    const from = previous.current
    if (from === value) return
    previous.current = value

    if (reduced) {
      setDisplay(value)
      return
    }

    if (flash) {
      setDelta(value > from ? 'up' : 'down')
    }

    const controls = animate(from, value, {
      duration: 0.6,
      ease: 'easeOut',
      onUpdate: v => setDisplay(Math.round(v)),
      onComplete: () => setDelta(null),
    })
    return () => controls.stop()
  }, [value, flash, reduced])

  return (
    <span
      className={className}
      style={{
        ...style,
        transition: 'color 0.3s ease',
        ...(delta === 'up' ? { color: 'var(--success)' } : {}),
        ...(delta === 'down' ? { color: 'var(--danger)' } : {}),
      }}
    >
      {display.toLocaleString('es-ES')}{suffix}
    </span>
  )
}
